import { motion } from 'framer-motion'
import { 
  MapPin, 
  Globe, 
  Shield,
  Cpu,
  Terminal,
  Activity
} from 'lucide-react'

const About = () => {
  const companyValues = [
    {
      title: "Rzetelność bez kompromisów",
      description: "Każde zlecenie transportowe i spedycyjne traktujemy indywidualnie. Gwarantujemy stuprocentowe bezpieczeństwo ładunku oraz pełne wsparcie formalne."
    },
    {
      title: "Punktualność i precyzja",
      description: "Czas to pieniądz. Dzięki optymalnemu planowaniu tras i doświadczeniu kierowców, dostarczamy towary zawsze na czas, zgodnie z ustalonym harmonogramem."
    },
    {
      title: "Elastyczność B2B",
      description: "Doskonale rozumiemy, że potrzeby logistyczne mogą ulec nagłej zmianie. Błyskawicznie reagujemy na modyfikacje zleceń i dostosowujemy ofertę."
    },
    {
      title: "Partnerska współpraca",
      description: "Nie jesteśmy tylko wykonawcą – jesteśmy partnerem biznesowym. Budujemy wieloletnie, oparte na zaufaniu relacje z klientami i spedycjami."
    }
  ]

  const technologies = [
    {
      icon: <Terminal className="w-6 h-6 text-secondary-400" />,
      title: "Platforma Zarządzania Flotą",
      description: "Autorski system webowy do szybkiego przydzielania zleceń kierowcom oraz cyfrowego obiegu dokumentów w firmie (w fazie aktywnego wdrożenia).",
      status: "Wdrożenie"
    },
    {
      icon: <Activity className="w-6 h-6 text-secondary-400" />,
      title: "Zaawansowana Telemetria GPS",
      description: "Stały, całodobowy monitoring pozycji pojazdów w czasie rzeczywistym z automatycznym wyliczaniem przewidywanego czasu przyjazdu (ETA).",
      status: "Aktywne"
    },
    {
      icon: <Cpu className="w-6 h-6 text-secondary-400" />,
      title: "Automatyzacje API & n8n",
      description: "Zintegrowane przepływy danych za pomocą zaawansowanych scenariuszy n8n, łączące formularze, bazy danych oraz systemy rozliczeniowe.",
      status: "Aktywne"
    }
  ]

  return (
    <div className="min-h-screen bg-slate-50/50 overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-850 to-primary-950 text-white py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(2,132,199,0.1),transparent_50%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-widest bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">Poznaj FG Falke</span>
            <h1 className="text-4xl md:text-6xl font-extrabold font-display tracking-tight leading-tight">
              O Firmie FG Falke
            </h1>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Naszą pasją jest transport, a celem – budowanie najefektywniejszych korytarzy transportowych łączących Polskę, Szwajcarię i Niemcy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Story Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6 text-slate-600"
            >
              <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Nasza Historia</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
                Sokół w Służbie Nowoczesnej Logistyki
              </h2>
              <p className="text-base md:text-lg leading-relaxed">
                Nazwa **FG Falke** nie jest przypadkowa – nawiązuje do symbolu sokoła (*niem. Falke*), oznaczającego precyzję, wyjątkową szybkość reakcji oraz pełną niezależność w działaniu. 
              </p>
              <p className="text-base leading-relaxed">
                Firma została założona w **2018 roku** przez Mariusza Sokołowskiego. Posiadamy ponad **7 lat praktycznego doświadczenia** w koordynowaniu przewozów całopojazdowych oraz drobnicowych na wymagającym, transgranicznym rynku Szwajcarii i Unii Europejskiej.
              </p>
              <p className="text-base leading-relaxed">
                Dzięki doskonałej znajomości specyfiki odpraw celnych, szwajcarskich opłat drogowych (*LSVA*) oraz restrykcyjnego prawa pracy kierowców, gwarantujemy pełne bezpieczeństwo ładunków oraz stuprocentową zgodność z prawem.
              </p>
            </motion.div>

            {/* Info Box Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="glass-card p-8 border-l-4 border-l-primary-600 shadow-xl relative overflow-hidden">
                <h3 className="text-2xl font-bold font-display text-slate-900 mb-6">Dane rejestrowe & Siedziba</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-100 p-2.5 rounded-xl text-primary-600 shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Siedziba główna</p>
                      <p className="text-sm font-semibold text-slate-800">Szczurowa, Województwo Małopolskie, Polska</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-100 p-2.5 rounded-xl text-primary-600 shrink-0">
                      <Globe className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Główne kierunki</p>
                      <p className="text-sm font-semibold text-slate-800">Szwajcaria (CH), Niemcy (DE), Polska (PL)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-100 p-2.5 rounded-xl text-primary-600 shrink-0">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Forma prawna</p>
                      <p className="text-sm font-semibold text-slate-800">Jednoosobowa Działalność Gospodarcza (JDG)</p>
                      <p className="text-xs text-slate-500 mt-1">Pełna odpowiedzialność ubezpieczeniowa oraz licencyjna B2B.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50 relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-xs font-extrabold text-primary-600 uppercase tracking-widest bg-primary-100/60 py-1.5 px-4 rounded-full">Filary Działania</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4 mb-6">
              Wartości, Którymi Się Kierujemy
            </h2>
            <p className="text-lg text-slate-500">
              Budowanie długotrwałego sukcesu w transporcie wymaga żelaznych zasad. Oto wartości, które wdrażamy w każdym pojedynczym zleceniu.
            </p>
          </motion.div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {companyValues.map((val, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-md card-interactive"
              >
                <h3 className="text-xl font-bold text-slate-900 font-display mb-3 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-secondary-500"></span>
                  {val.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{val.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology & Automation Section (SaaS Theme) */}
      <section className="section-padding bg-slate-900 text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(2,132,199,0.1),transparent_70%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20 space-y-4"
          >
            <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-wider bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">
              FG Falke Tech-Stack
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold font-display tracking-tight">
              Nowoczesne Technologie Logistyczne
            </h2>
            <p className="text-lg text-primary-200/80 font-light leading-relaxed">
              Nie jesteśmy tradycyjną firmą transportową. Rozwijamy własne narzędzia IT i automatyzujemy przepływ danych w celu zminimalizowania błędów ludzkich.
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technologies.map((tech, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="glass-card-dark p-8 border border-white/5 hover:border-secondary-500/30 transition-all duration-300 relative group"
              >
                {/* Badge Status */}
                <span className="absolute top-6 right-6 text-[10px] font-extrabold uppercase tracking-widest bg-secondary-500/10 border border-secondary-500/30 text-secondary-300 px-2.5 py-0.5 rounded-full">
                  {tech.status}
                </span>

                <div className="bg-white/5 w-12 h-12 rounded-xl flex items-center justify-center mb-6 border border-white/10 group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>

                <h3 className="text-xl font-bold font-display text-white mb-3">
                  {tech.title}
                </h3>
                <p className="text-primary-200/70 text-sm leading-relaxed">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
